import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import PageBack from '../ui/PageBack';
import {
  blastElementId,
  runBlastRadiusForward,
  type BlastRadiusScoreDocument,
} from '../api-utils/blast';
import type { BusinessElementDocument, ThreatElementDocument } from '../api-utils/elements';
import { getDataSource } from '../data';
import { combine } from '../data/queries/combine';
import { useDataClock } from '../data/queries/DataProvider';
import { useBusinessElements, useInfrastructureElements, useThreatElements } from '../data/queries/hooks';
import EffectiveBlastRadiusMap from '../components/c3/EffectiveBlastRadiusMap';
import type { ForceGraphNode } from '../components/c3/correlationGraph';
import { ebrScoreOf, titleCase } from '../components/c3/dossierModel';
import { formatClock, scoreCls, scoreColor } from '../components/c3/eriUtils';

export interface EbrDetailPageProps {
  score: BlastRadiusScoreDocument;
  onBack: () => void;
  /** Jump to Investigation Workspace graph focused on this node. */
  onOpenInGraph: (id: string, name: string) => void;
  /** Notify parent when score was refreshed after recalculate. */
  onScoreUpdated?: (score: BlastRadiusScoreDocument) => void;
}

function infraToInventory(
  docs: Array<{
    _id?: string;
    name?: string;
    universal_domain?: string;
    universal_type?: string;
    provider?: string;
    provider_type?: string;
    external_id?: string;
  }>
): ForceGraphNode[] {
  const out: ForceGraphNode[] = [];
  for (const doc of docs) {
    const id = String(doc._id || '').trim();
    if (!id) continue;
    const label = String(doc.name || doc.external_id || id);
    const group = String(doc.universal_domain || 'other').toLowerCase();
    out.push({
      id,
      label,
      group,
      universalType: String(doc.universal_type || 'element'),
      universalDomain: group,
      provider: String(doc.provider || ''),
      providerType: String(doc.provider_type || ''),
      externalId: String(doc.external_id || id),
      name: label,
      linkCount: 0,
      layer: 'infrastructure',
    });
  }
  return out;
}

const EbrDetailPage: React.FC<EbrDetailPageProps> = ({
  score: initialScore,
  onBack,
  onOpenInGraph,
  onScoreUpdated,
}) => {
  const [score, setScore] = useState(initialScore);
  // The inventory comes from the shared cache and the one refresh clock; Demo reads the invented tenant.
  const { refreshNow } = useDataClock();
  const infraRead = useInfrastructureElements();
  const businessRead = useBusinessElements();
  const threatsRead = useThreatElements();
  const inventoryState = combine([infraRead, businessRead, threatsRead]);
  const usable = inventoryState.ready && !inventoryState.failed;
  const infraRows = infraRead.data;
  const businessRows = businessRead.data;
  const threatRows = threatsRead.data;
  const inventory: ForceGraphNode[] = useMemo(
    () => (usable ? infraToInventory(infraRows ?? []) : []),
    [usable, infraRows]
  );
  const business: BusinessElementDocument[] = useMemo(
    () => (usable ? businessRows ?? [] : []),
    [usable, businessRows]
  );
  const threats: ThreatElementDocument[] = useMemo(
    () => (usable ? threatRows ?? [] : []),
    [usable, threatRows]
  );
  const inventoryLoading = inventoryState.loading;
  const [recalcStatus, setRecalcStatus] = useState<'idle' | 'running' | 'ok' | 'error'>('idle');
  const [recalcError, setRecalcError] = useState<string | null>(null);
  const [liveWalk, setLiveWalk] = useState<unknown>(null);

  const startId = blastElementId(score);
  const ebr = ebrScoreOf(score) ?? 0;

  useEffect(() => {
    setScore(initialScore);
  }, [initialScore]);

  const refreshScoreFromRing = useCallback(async () => {
    const id = blastElementId(score) || startId;
    if (!id) return;
    // A read after a write must be fresh, so it asks the source and then tells the cache.
    const rows = await getDataSource().listBlastRadiusScores();
    void refreshNow();
    const match = rows.find((row) => blastElementId(row) === id);
    if (match) {
      setScore(match);
      onScoreUpdated?.(match);
    }
  }, [score, startId, onScoreUpdated, refreshNow]);

  const handleRecalculate = useCallback(async () => {
    if (!startId || recalcStatus === 'running') return;
    setRecalcStatus('running');
    setRecalcError(null);
    try {
      const result = await runBlastRadiusForward(startId);
      if (!result.success) {
        throw new Error('blast_radius.run_forward returned success=false');
      }
      setLiveWalk(result.raw);
      await refreshScoreFromRing();
      setRecalcStatus('ok');
    } catch (err) {
      setRecalcStatus('error');
      setRecalcError(err instanceof Error ? err.message : String(err));
    }
  }, [startId, recalcStatus, refreshScoreFromRing]);

  const subtitle = useMemo(
    () =>
      [
        score.band,
        score.max_domain ? `max ${titleCase(String(score.max_domain))}` : '',
        `reach ${Number(score.reachability_count || 0).toLocaleString('en-US')}`,
        score.truncated === true || String(score.truncated) === 'True' ? 'truncated' : '',
        score.computed_at ? `updated ${formatClock(score.computed_at)}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    [score]
  );

  const header = (
    <>
      <PageBack onClick={onBack} />
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold truncate m-0">
            {score.element_name || startId || 'Element'}
          </h1>
          <p className="text-[13px] text-c3-mute m-0 mt-0.5">{subtitle || '—'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className={`text-[28px] font-bold leading-none ${scoreCls(ebr)}`}>
              {Math.round(ebr)}
            </div>
            <div className="eri-bar w-28 mt-1">
              <i
                style={{
                  width: `${Math.max(0, Math.min(100, ebr))}%`,
                  background: scoreColor(ebr),
                }}
              />
            </div>
          </div>
          <button
            type="button"
            className="c3-pill"
            disabled={!startId || recalcStatus === 'running'}
            onClick={() => void handleRecalculate()}
            title="Re-run blast_radius.run_forward for this element"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${recalcStatus === 'running' ? 'animate-spin' : ''}`}
            />
            {recalcStatus === 'running' ? 'Recalculating…' : 'Recalculate EBR'}
          </button>
        </div>
      </div>
      {recalcStatus === 'ok' ? (
        <p className="text-[13px] text-c3-mute m-0 mt-2">EBR recalculated and score refreshed.</p>
      ) : null}
      {recalcStatus === 'error' && recalcError ? (
        <p className="text-[13px] text-c3-red m-0 mt-2">{recalcError}</p>
      ) : null}
    </>
  );

  return (
    <section className="c3-band c3-ebr-detail">
      <h2>
        Effective Blast Radius
        <span className="meta">predecessor walk · left to right</span>
      </h2>
      {inventoryLoading ? (
        <div className="space-y-3">
          <div className="c3-ebr-head">{header}</div>
          <p className="text-c3-mute text-sm">Loading inventory labels…</p>
        </div>
      ) : (
        <EffectiveBlastRadiusMap
          score={score}
          inventory={inventory}
          business={business}
          threats={threats}
          liveWalk={liveWalk}
          onBack={onBack}
          onOpenEntity={onOpenInGraph}
          backLabel="Back"
          hideHeaderMeta
          header={header}
        />
      )}
    </section>
  );
};

export default EbrDetailPage;
